import type { IIssueApi, ISortColumn, IStoredIssueColConfigs } from "@schemas/issueSchema";
import type { ColDef } from "ag-grid-community";
import { useCallback, useMemo, useState } from "react";
import { issueBoardHasClientAccess } from "../../issueUtils";
import {
  createDateColumnEditor as createDateColumnEditorFactory,
  createHideAndSort,
  createIssueColumnBaseDefGetter,
  createUserColumnEditor as createUserColumnEditorFactory,
} from "./columns/issueColumnHelpers";
import { getIssueBaseColumns } from "./columns/issueBaseColumns";
import { buildIssueColumnGroups } from "./columns/issueColumnGroups";
import { getAgGridOtherAttributeColumns as buildOtherAttributeColumns } from "./columns/issueOtherAttributeColumns";
import { getRisikoEvaluationColumns as buildRisikoEvaluationColumns } from "./columns/issueRisikoColumns";
import type { IssueTableColumnContext } from "./columns/issueTableColumnContext";
import type { IIssueTableAgGridColumnProps } from "./issueTableTypes";

export const useIssueTableColumns = ({
  columnProps,
  columnsVisibility,
  sortedCol,
  columnState,
  updateIssue,
}: {
  columnProps: IIssueTableAgGridColumnProps;
  columnsVisibility: string[];
  sortedCol: ISortColumn | null;
  columnState?: IStoredIssueColConfigs[];
  updateIssue: (args: any) => any;
}) => {
  const {
    boardBuckets,
    setSelectedIssue,
    types,
    pnsItems,
    members,
    issueBoardId,
    projectId,
    updateItemMembers,
    otherAttributesConfig,
    issueBoard,
  } = columnProps;
  const boardHasClientAccess = issueBoardHasClientAccess(issueBoard);
  const hideAndSort = useMemo(
    () => createHideAndSort({ columnsVisibility, issueBoard, sortedCol }),
    [columnsVisibility, issueBoard, sortedCol],
  );
  const getIssueColumnBaseDef = useMemo(() => createIssueColumnBaseDefGetter(hideAndSort), [hideAndSort]);
  const createDateColumnEditor = useMemo(() => createDateColumnEditorFactory(), []);
  const createUserColumnEditor = useMemo(() => createUserColumnEditorFactory(members), [members]);
  const [unlockedOtherAttributeCells, setUnlockedOtherAttributeCells] = useState<Set<string>>(() => new Set());

  const getOtherAttributeCellKey = useCallback((issueId: string, attr: string) => `${issueId}:${attr}`, []);
  const isOtherAttributeCellUnlocked = useCallback(
    (issueId: string, attr: string) => unlockedOtherAttributeCells.has(getOtherAttributeCellKey(issueId, attr)),
    [getOtherAttributeCellKey, unlockedOtherAttributeCells],
  );
  const unlockOtherAttributeCell = useCallback(
    (issueId: string, attr: string) => {
      setUnlockedOtherAttributeCells((prev) => {
        const next = new Set(prev);
        next.add(getOtherAttributeCellKey(issueId, attr));
        return next;
      });
    },
    [getOtherAttributeCellKey],
  );
  const lockOtherAttributeCell = useCallback(
    (issueId: string, attr: string) => {
      setUnlockedOtherAttributeCells((prev) => {
        const next = new Set(prev);
        next.delete(getOtherAttributeCellKey(issueId, attr));
        return next;
      });
    },
    [getOtherAttributeCellKey],
  );

  const handleUpdateIssueField = useCallback(
    async (issueId: string, attr: string, value: any, oldValue: any = "") => {
      await updateIssue({
        projectId,
        issueBoardId,
        issueId,
        attr,
        value,
        oldValue,
      });
    },
    [projectId, issueBoardId, updateIssue],
  );

  const columnContext: IssueTableColumnContext = useMemo(
    () => ({
      boardBuckets,
      boardHasClientAccess,
      createDateColumnEditor,
      createUserColumnEditor,
      getIssueColumnBaseDef,
      handleUpdateIssueField,
      hideAndSort,
      isOtherAttributeCellUnlocked,
      issueBoard,
      issueBoardId,
      lockOtherAttributeCell,
      members,
      pnsItems,
      projectId,
      setSelectedIssue,
      types,
      unlockOtherAttributeCell,
      updateItemMembers,
    }),
    [
      boardBuckets,
      boardHasClientAccess,
      createDateColumnEditor,
      createUserColumnEditor,
      getIssueColumnBaseDef,
      handleUpdateIssueField,
      hideAndSort,
      isOtherAttributeCellUnlocked,
      issueBoard,
      issueBoardId,
      lockOtherAttributeCell,
      members,
      pnsItems,
      projectId,
      setSelectedIssue,
      types,
      unlockOtherAttributeCell,
      updateItemMembers,
    ],
  );

  const columns: ColDef<IIssueApi>[] = useMemo(() => getIssueBaseColumns(columnContext), [columnContext]);
  const otherAttributeColumns = useMemo(
    () =>
      buildOtherAttributeColumns({
        columnContext,
        otherAttributesConfig,
      }),
    [columnContext, otherAttributesConfig],
  );
  const risikoColumns = useMemo(
    () =>
      issueBoard?.risikoSection?.isActive
        ? buildRisikoEvaluationColumns({
            columnContext,
          })
        : [],
    [columnContext, issueBoard?.risikoSection?.isActive],
  );
  const columnConfigById = useMemo(
    () =>
      new Map(
        (columnState ?? [])
          .filter((columnConfig) => columnConfig.colId != null)
          .map((columnConfig) => [columnConfig.colId!, columnConfig]),
      ),
    [columnState],
  );

  const groupedColumns: ColDef<IIssueApi>[] = useMemo(
    () =>
      applyStoredColumnWidths(
        buildIssueColumnGroups({
          columns: [...columns, ...risikoColumns],
          columnsVisibility,
          issueBoard,
          otherAttributeColumns,
        }),
        columnConfigById,
      ),
    [columns, columnsVisibility, columnConfigById, issueBoard, otherAttributeColumns, risikoColumns],
  );

  return {
    columnContext,
    columns,
    groupedColumns,
  };
};

const getColumnId = (colDef: ColDef<IIssueApi>): string | undefined => {
  if (colDef.colId != null) {
    return colDef.colId;
  }

  return typeof colDef.field === "string" ? colDef.field : undefined;
};

const getColumnOrderPriority = (
  colDef: ColDef<IIssueApi>,
  columnConfigById: Map<string, IStoredIssueColConfigs>,
): number => {
  const columnId = getColumnId(colDef);
  if (columnId == null) {
    return Number.MAX_SAFE_INTEGER;
  }

  return columnConfigById.get(columnId)?.orderPriority ?? Number.MAX_SAFE_INTEGER;
};

const applyStoredColumnWidths = (
  columnDefs: ColDef<IIssueApi>[],
  columnConfigById: Map<string, IStoredIssueColConfigs>,
): ColDef<IIssueApi>[] => {
  return columnDefs.map((colDef) => {
    if ("children" in colDef && Array.isArray(colDef.children)) {
      return {
        ...colDef,
        children: applyStoredColumnWidths(colDef.children as ColDef<IIssueApi>[], columnConfigById).sort(
          (left, right) =>
            getColumnOrderPriority(left, columnConfigById) - getColumnOrderPriority(right, columnConfigById),
        ),
      };
    }

    const columnId = getColumnId(colDef);
    const storedWidth = columnId == null ? undefined : columnConfigById.get(columnId)?.width;

    if (storedWidth == null || storedWidth <= 0) {
      return colDef;
    }

    return {
      ...colDef,
      flex: undefined,
      width: storedWidth,
    };
  });
};
